import React, { useEffect, useState } from "react";
import { X, User as UserIcon, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const NOTE_TEMPLATES = [
  {
    label: "Overloaded",
    text: "I'm overloaded this week — please cover this file, docs look clean. Ping me if you spot anything odd.",
  },
  {
    label: "Domain expertise",
    text: "This one needs your domain knowledge — sector-specific compliance question in the KYC docs.",
  },
  {
    label: "Escalation",
    text: "Escalating this — founder response times are outside SLA. Please push for a decision.",
  },
  {
    label: "Custom",
    text: "",
  },
];

/**
 * HandoffModal — replaces the browser `prompt` used for KYC reassignment.
 * Features:
 *   - Reviewer autocomplete (fuzzy search across admin/manager/super_admin users)
 *   - 4 preset note templates ("Overloaded", "Domain expertise", "Escalation", "Custom")
 *   - Free-form note textarea
 *
 * Props:
 *   open, onClose, onConfirm({ user_id, handoff_note })
 *   currentAssigneeId — pre-selected reviewer (may be undefined)
 */
export default function HandoffModal({ open, onClose, onConfirm, currentAssigneeId }) {
  const [reviewers, setReviewers] = useState([]);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(currentAssigneeId || null);
  const [note, setNote] = useState("");
  const [templateKey, setTemplateKey] = useState("Overloaded");
  const [busy, setBusy] = useState(false);

  // Whenever the modal is (re)opened, refresh preselected reviewer + preset note.
  useEffect(() => {
    if (!open) return;
    setSelectedId(currentAssigneeId || null);
    setTemplateKey("Overloaded");
    const t = NOTE_TEMPLATES.find((x) => x.label === "Overloaded");
    setNote(t?.text || "");
    setQ("");
  }, [open, currentAssigneeId]);

  // Wire the reviewer list once per open.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await api.get("/admin/users?role=admin&role=manager&role=super_admin&limit=200");
        // The endpoint responds with either {items:[...]} or a flat array.
        const items = res.data.items || res.data || [];
        setReviewers(items);
      } catch (e) { /* silent */ }
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const query = q.trim().toLowerCase();
  const filtered = query
    ? reviewers.filter((r) => (r.name || "").toLowerCase().includes(query)
                          || (r.email || "").toLowerCase().includes(query))
    : reviewers;

  const applyTemplate = (label) => {
    setTemplateKey(label);
    const t = NOTE_TEMPLATES.find((x) => x.label === label);
    if (t && t.text) setNote(t.text);
    if (label === "Custom") setNote("");
  };

  const submit = async () => {
    if (!selectedId) { toast.info("Pick a reviewer first."); return; }
    if (note.trim().length < 3) { toast.info("Add a hand-off note (min 3 chars)."); return; }
    setBusy(true);
    try {
      await onConfirm?.({ user_id: selectedId, handoff_note: note.trim() });
      onClose?.();
    } catch (e) { /* upstream toast */ }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4"
      data-testid="handoff-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-xl shadow-2xl border border-navy/10 max-w-lg w-full">
        <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
          <div>
            <div className="overline text-emerald-700">Hand-off</div>
            <div className="font-heading text-lg text-ink">Reassign to another reviewer</div>
          </div>
          <button
            onClick={onClose}
            className="text-ink-muted hover:text-ink"
            aria-label="Close hand-off modal"
            data-testid="handoff-close"
          ><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Reviewer autocomplete */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Reviewer</label>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full mt-1 px-3 py-2 border border-navy/15 rounded-md text-sm text-ink focus:outline-none focus:ring-1 focus:ring-emerald-400"
              data-testid="handoff-reviewer-search"
            />
            <div className="mt-1 max-h-40 overflow-y-auto border border-navy/10 rounded-md" data-testid="handoff-reviewer-list">
              {filtered.length === 0 && (
                <div className="p-3 text-sm text-ink-muted italic">No reviewers found.</div>
              )}
              {filtered.map((r) => (
                <button
                  key={r.user_id}
                  type="button"
                  onClick={() => setSelectedId(r.user_id)}
                  className={`w-full text-left px-3 py-2 border-b border-navy/[0.05] last:border-b-0 flex items-center gap-2 ${
                    selectedId === r.user_id
                      ? "bg-emerald-50 text-emerald-800"
                      : "hover:bg-navy/[0.02]"
                  }`}
                  data-testid={`handoff-reviewer-${r.user_id}`}
                >
                  <UserIcon size={13} className={selectedId === r.user_id ? "text-emerald-700" : "text-ink-muted"} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-heading truncate">{r.name || r.email}</div>
                    <div className="text-[11px] font-mono text-ink-muted truncate">
                      {r.email} · {r.role}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Note template picker */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Note template</label>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {NOTE_TEMPLATES.map((t) => (
                <button
                  key={t.label}
                  type="button"
                  onClick={() => applyTemplate(t.label)}
                  className={`text-[11px] px-2 py-1 rounded-full border transition-colors ${
                    templateKey === t.label
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : "border-navy/15 text-ink-muted hover:bg-navy/[0.04]"
                  }`}
                  data-testid={`handoff-template-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                >{t.label}</button>
              ))}
            </div>
          </div>

          {/* Note text */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Hand-off note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="What should the receiver know?"
              className="w-full mt-1 px-3 py-2 border border-navy/15 rounded-md text-sm text-ink resize-none focus:outline-none focus:ring-1 focus:ring-emerald-400"
              data-testid="handoff-note"
            />
            <div className="text-[10px] text-ink-muted mt-0.5 text-right font-mono">
              {note.length}/1000
            </div>
          </div>
        </div>

        <div className="px-6 py-3 border-t border-navy/10 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="text-sm text-ink-muted hover:text-ink px-3 py-2"
            data-testid="handoff-cancel"
          >Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-heading inline-flex items-center gap-1.5 disabled:opacity-60"
            data-testid="handoff-confirm"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? "Reassigning…" : "Reassign with note"}
          </button>
        </div>
      </div>
    </div>
  );
}
